import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { Check, X, FileText, ChevronLeft, ChevronRight, Download } from 'lucide-react';

interface VendorInvoice {
  _id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  totalAmount: number;
  paidAmount?: number;
  remainingAmount?: number;
  vendorAccepted?: boolean | null;
  paymentStatus: 'PENDING' | 'PARTIALLY_PAID' | 'PAID';
  pdfPath?: string;
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  PARTIALLY_PAID: 'Partially Paid',
  PAID: 'Paid',
};

const MAX_REASON_LENGTH = 500;

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<VendorInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ open: boolean; invoiceId: string; invoiceNumber: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getMyVendorInvoices({ page, limit: 10 });
      const res = response.data as any;
      if (res?.success && res?.data !== undefined) {
        const list = Array.isArray(res.data) ? res.data : [];
        setInvoices(list);
        const pag = res.pagination;
        if (pag) {
          setTotalPages(pag.totalPages ?? 1);
          setTotal(pag.total ?? 0);
        }
      } else {
        setInvoices([]);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const getPdfUrl = async (id: string): Promise<string | null> => {
    try {
      const response = await apiService.getVendorInvoicePdfUrl(id);
      const res = response.data as any;
      return res?.data?.pdfUrl ?? null;
    } catch {
      return null;
    }
  };

  const showToast = (message: string, type: 'error' | 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDownloadPdf = async (id: string) => {
    setActionLoading(id);
    const url = await getPdfUrl(id);
    setActionLoading(null);
    if (!url) {
      showToast('PDF not available for this invoice.', 'error');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${id}.pdf`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    showToast('Download started.', 'success');
  };

  const handleAccept = async (id: string) => {
    setActionLoading(id);
    try {
      await apiService.acknowledgeVendorInvoice(id);
      await fetchInvoices();
      showToast('Invoice accepted successfully.', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Failed to accept invoice.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (inv: VendorInvoice) => {
    setRejectModal({ open: true, invoiceId: inv._id, invoiceNumber: inv.invoiceNumber });
    setRejectReason('');
    setRejectError(null);
  };

  const closeRejectModal = () => {
    if (!rejectSubmitting) {
      setRejectModal(null);
      setRejectReason('');
      setRejectError(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal) return;
    setRejectSubmitting(true);
    setRejectError(null);
    try {
      await apiService.rejectVendorInvoice(rejectModal.invoiceId, rejectReason.trim() || undefined);
      await fetchInvoices();
      closeRejectModal();
      setRejectModal(null);
      showToast('Invoice rejected. Dispute has been raised for review.', 'success');
    } catch (err: any) {
      setRejectError(err?.message || 'Failed to reject invoice.');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const pendingAcceptReject = (inv: VendorInvoice) => inv.vendorAccepted === null || inv.vendorAccepted === undefined;

  return (
    <div className="p-6 bg-gray-100 min-h-full">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Invoice List</h1>
        <p className="text-sm text-gray-500 mt-1">
          Invoices generated for you from Finops. View, download, accept or reject.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-100 text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="inline-block w-8 h-8 border-2 border-jd-green border-t-transparent rounded-full animate-spin" />
            <p className="mt-2">Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No invoices found.</p>
            <p className="text-sm mt-1">Invoices generated from Finops will appear here.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount (₹)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Vendor Accepted</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((inv) => (
                    <tr key={inv._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{inv.invoiceNumber}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        ₹{typeof inv.totalAmount === 'number' ? inv.totalAmount.toLocaleString('en-IN') : inv.totalAmount ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            inv.vendorAccepted === true
                              ? 'bg-green-100 text-green-800'
                              : inv.vendorAccepted === false
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {inv.vendorAccepted === true ? 'Yes' : inv.vendorAccepted === false ? 'No' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            inv.paymentStatus === 'PAID'
                              ? 'bg-green-100 text-green-800'
                              : inv.paymentStatus === 'PARTIALLY_PAID'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {PAYMENT_STATUS_LABELS[inv.paymentStatus] || inv.paymentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <button
                            onClick={() => handleDownloadPdf(inv._id)}
                            disabled={actionLoading === inv._id}
                            className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {pendingAcceptReject(inv) && (
                            <>
                              <button
                                onClick={() => handleAccept(inv._id)}
                                disabled={actionLoading === inv._id}
                                className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                <Check className="w-4 h-4 mr-0.5" /> Accept
                              </button>
                              <button
                                onClick={() => openRejectModal(inv)}
                                disabled={actionLoading === inv._id}
                                className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                              >
                                <X className="w-4 h-4 mr-0.5" /> Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Page {page} of {totalPages} ({total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reject reason modal */}
      {rejectModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeRejectModal}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reject Invoice</h3>
            <p className="text-sm text-gray-500 mb-4">
              Invoice {rejectModal.invoiceNumber} will be rejected and a dispute will be raised for review.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for rejection (optional)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
              placeholder="e.g. Incorrect amount, missing items..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              disabled={rejectSubmitting}
            />
            <p className="text-xs text-gray-400 mt-1 mb-4">
              {rejectReason.length}/{MAX_REASON_LENGTH}
            </p>
            {rejectError && (
              <p className="text-sm text-red-600 mb-4">{rejectError}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeRejectModal}
                disabled={rejectSubmitting}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectSubmit}
                disabled={rejectSubmitting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {rejectSubmitting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
